/* eslint-disable max-len */
/* eslint-disable no-unused-vars */
/* eslint-disable no-console */
import { Box, Stack, TextField, Typography } from '@mui/material';
import React, { useContext, useState, useEffect } from 'react';
import { MdCrop } from 'react-icons/md';
import { RiDeleteBinLine } from 'react-icons/ri';
import { TbReplace } from 'react-icons/tb';
import { LanguageContext } from '../../../../../../../../../../../libs/Context';
import ProfilePic from '../../../../../../../../../../../components/Common/ProfilePic';
import UploadDialog from '../../../../UploadDialog';
import NewDialogPopup from '../../../../../../../../../../../components/Common/NewDialogPopup/NewDialogPopup';
import { s3DeleteNew } from '../../../../../../../../../../../libs/HelperContent';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import CropDialog from './CropDialog';

// Add interface for the data props
interface DetailsData {
	title: string;
	description: string;
	thumbnail?: {
		url: string;
		canEdit?: boolean;
		canDelete?: boolean;
		source?: string;
		key?: string;
	};
}

interface DetailsSliderProps {
	articleCreate?: string;
	data?: DetailsData;
	onChange?: (updatedData: Partial<DetailsData>) => void;
	itemType?: 'podcast' | 'season' | 'episode' | 'image' | 'video' | 'document' | 'playlist' | 'article';
}

// UI state interface - Enhanced to include crop functionality
interface UIState {
	hover: boolean;
	uploadDialogOpen: boolean;
	deleteDialogOpen: boolean;
	cropDialogOpen: boolean; // NEW: Added crop dialog state
	isReplacingThumbnail: boolean; // NEW: Track when replacing thumbnail
}

const DetailsSlider: React.FC<DetailsSliderProps> = (props) =>
{
	// Combined UI state - Enhanced with crop functionality
	const [ uiState, setUIState ] = useState<UIState>({
		hover                : false,
		uploadDialogOpen     : false,
		deleteDialogOpen     : false,
		cropDialogOpen       : false, // NEW: Initialize crop dialog state
		isReplacingThumbnail : false // NEW: Initialize replacing thumbnail state
	});

	const { language } = useContext(LanguageContext);
	const { articleCreate, data = { title: '', description: '', thumbnail: { url: '' } }, onChange, itemType = 'podcast' } = props;

	const userData = useSelector((state:any) => state.mysession?.session);

	// Ensure we always have valid data with fallbacks
	const currentData = {
		title       : data?.title || '',
		description : data?.description || '',
		thumbnail   : data?.thumbnail || { url: '' }
	};

	// console.log('DetailsSlider - Current data:', currentData);
	// console.log('DetailsSlider - onChange function:', Boolean(onChange));

	// UI state helpers
	const updateUIState = (updates: Partial<UIState>) =>
	{
		setUIState((prevState) => ({ ...prevState, ...updates }));
	};

	// Character count tracking
	const titleMaxLength = 50;
	const descriptionMaxLength = 250;

	// Update parent component when values change
	const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
	{
		const newTitle = e.target.value;

		// console.log('DetailsSlider - Title changing to:', newTitle);

		if (onChange)
		{
			onChange({ title: newTitle });
		}
		else
		{
			console.warn('DetailsSlider - No onChange handler provided for title change');
		}
	};

	const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) =>
	{
		const newDescription = e.target.value;

		if (onChange)
		{
			onChange({ description: newDescription });
		}
		else
		{
			console.warn('DetailsSlider - No onChange handler provided for description change');
		}
	};

	// ENHANCED: Hide thumbnail for image and document content types
	const shouldShowThumbnail = () =>
	{
		// Images don't need thumbnails - the image itself is the visual representation
		// Documents don't need thumbnails - file icons and auto-previews are sufficient
		return itemType === 'podcast' || itemType === 'episode' || itemType === 'video' || itemType === 'playlist' || itemType === 'article';
	};

	// Modify handleThumbnailUpdate to respect item type
	const handleThumbnailUpdate = (newUrl: string, source?: string, key?: string) =>
	{
		const newThumbnail = {
			url       : newUrl,
			canEdit   : currentData.thumbnail?.canEdit || false,
			canDelete : currentData.thumbnail?.canDelete || false,
			source    : source || 'local',
			key
		};

		console.log('DetailsSlider - Thumbnail updating to:', newThumbnail);

		if (onChange)
		{
			onChange({ thumbnail: newThumbnail });
		}
		else
		{
			console.warn('DetailsSlider - No onChange handler provided for thumbnail change');
		}
	};

	// Delete file from S3
	const deleteFileFromS3 = async (key?: string, showToast: boolean = true) =>
	{
		if (!key)
		{
			console.warn('No key provided for S3 deletion');

			return;
		}

		try
		{
			await s3DeleteNew(key, '');
			if (showToast)
			{
				toast.success('Thumbnail deleted successfully');
			}
		}
		catch (error: any)
		{
			console.error('Error deleting file from S3:', error);
			if (showToast)
			{
				toast.error(`Failed to delete thumbnail: ${error.message || 'Unknown error'}`);
			}
		}
	};

	// Modify handleReplaceThumbnail to respect item type
	const handleReplaceThumbnail = () =>
	{
		updateUIState({ uploadDialogOpen: true });
	};

	// ENHANCED: Implement crop functionality with react-advanced-cropper
	const handleCropThumbnail = () =>
	{
		// Check if there's a thumbnail to crop
		if (!currentData.thumbnail?.url)
		{
			toast.warning('Please upload a thumbnail first before cropping');

			return;
		}

		// Open crop dialog
		updateUIState({ cropDialogOpen: true });
		console.log('DetailsSlider - Opening crop dialog for:', currentData.thumbnail.url);
	};

	// ENHANCED: Handle crop completion with real implementation
	const handleCropComplete = (croppedImageUrl: string, croppedImageKey?: string) =>
	{
		console.log('DetailsSlider - Crop completed:', croppedImageUrl);

		// Set replacing thumbnail state to suppress delete toast
		updateUIState({ isReplacingThumbnail: true });

		// If we're replacing an existing thumbnail from S3, delete the old one without showing toast
		if (currentData.thumbnail?.source === 'system' && currentData.thumbnail?.key)
		{
			deleteFileFromS3(currentData.thumbnail.key, false); // Suppress delete toast
		}

		// Update thumbnail with cropped image
		handleThumbnailUpdate(croppedImageUrl, 'system', croppedImageKey);

		// Reset replacing thumbnail state
		updateUIState({ isReplacingThumbnail: false, cropDialogOpen: false });
	};

	// ENHANCED: Handle crop cancellation
	const handleCropCancel = () =>
	{
		updateUIState({ cropDialogOpen: false });
		console.log('DetailsSlider - Crop operation cancelled');
	};

	// Modify handleDeleteThumbnail to respect item type
	const handleDeleteThumbnail = () =>
	{
		if (currentData.thumbnail?.source === 'system' && currentData.thumbnail?.key)
		{
			updateUIState({ deleteDialogOpen: true });
		}
		else
		{
			handleThumbnailUpdate('');
		}
	};

	// Check if thumbnail URL exists to conditionally show delete button
	const hasThumbnail = Boolean(currentData.thumbnail?.url);

	// COMMENTED OUT: Default thumbnail logic as per requirements
	// Add this function before the return statement
	const getImageSource = () =>
	{
		const thumbnailUrl = currentData.thumbnail?.url;

		// COMMENTED OUT: No longer using default images as per requirements
		/*
		if (itemType === 'podcast')
		{
			return thumbnailUrl || '/images/podcast_default.jpg';
		}

		if (itemType === 'playlist')
		{
			return thumbnailUrl || '/images/playlist_default.jpg';
		}

		return thumbnailUrl || 'https://static.vecteezy.com/system/resources/previews/050/496/422/non_2x/serene-landscape-featuring-a-vibrant-pink-tree-by-a-tranquil-lake-photo.jpeg';
		*/

		// NEW: Return only the actual thumbnail URL or empty string
		return thumbnailUrl || '';
	};

	// COMMENTED OUT: Default thumbnail detection logic
	// const isDefaultThumbnail = (itemType === 'podcast' || itemType === 'playlist') && !currentData.thumbnail?.url;

	// NEW: Check if we have an actual thumbnail (not default)
	const hasActualThumbnail = Boolean(currentData.thumbnail?.url);

	return (
		<>
			{articleCreate === 'Create' &&
			<><Typography fontSize={'var(--pixel12)'} fontWeight={500}>{language?.authorTxt}</Typography>
				<Stack direction={'row'} alignItems={'center'} width={'100%'} spacing={1} pt={0.5} pb={2}>
					<Box width={'32px'} height={'32px'}>
						<ProfilePic
							imagePath={`${userData?.userId}_profile.jpeg`}
							defaultImg='/images/icons/defaultUser.svg'
							alt='createdBy_img'
							className='brdrRadius50'
						/>
					</Box>
					<Typography
						fontWeight={500}
						noWrap
						sx={{
							overflow     : 'hidden',
							textOverflow : 'ellipsis',
							whiteSpace   : 'nowrap',
							minWidth     : '200px'
						}}
					>{userData?.name}</Typography>
				</Stack></>}
			<Typography variant='body2' color={'#353535'} fontWeight={'var(--font500)'}>{language?.title}*</Typography>
			<TextField
				fullWidth
				placeholder='Enter Title'
				margin='normal'
				size='small'
				value={currentData.title}
				onChange={handleTitleChange}
				inputProps={{ maxLength: titleMaxLength }}
			/>
			<Typography textAlign={'end'} color={'#707070'} fontSize={'var(--pixel12)'}>
				{currentData.title.length}/{titleMaxLength}
			</Typography>
			<Typography variant='body2' pt={0.5} fontWeight={'var(--font500)'}>
				{language?.description}
			</Typography>
			<TextField
				fullWidth
				multiline
				rows={3}
				placeholder={`${language?.enterDescript}...`}
				margin='normal'
				value={currentData.description}
				onChange={handleDescriptionChange}
				inputProps={{ maxLength: descriptionMaxLength }}
			/>
			<Typography textAlign={'end'} color={'#707070'} fontSize={'var(--pixel12)'}>
				{currentData.description.length}/{descriptionMaxLength}
			</Typography>
			{articleCreate === 'Create' &&
			<><Typography variant='body2' color={'#353535'} fontWeight={'var(--font500)'}>{language?.estimatedreadtime}</Typography>
				<TextField fullWidth defaultValue={'15:00 min'} margin='normal' size='small' />
				<Typography textAlign={'end'} color={'#707070'} fontSize={'var(--pixel12)'}>0/50</Typography>
			</>}

			{/* Conditionally render thumbnail section */}
			{shouldShowThumbnail() && (
				<>
					<Typography variant='body2' py={0.5} fontWeight={'var(--font500)'}>
						{language?.thumbailTxt}
					</Typography>

					{/* MODIFIED: Only show thumbnail container if we have an actual thumbnail */}
					{hasActualThumbnail
						? (
							<Box my={1}
								sx={{ width: '250px', height: '150px', position: 'relative', borderRadius: '13px', overflow: 'hidden' }}
								onMouseEnter={() => updateUIState({ hover: true })}
								onMouseLeave={() => updateUIState({ hover: false })}
							>
								<img
									src={getImageSource()}
									style={{
										width        : '100%',
										height       : '100%',
										objectFit    : 'cover', // Always use cover for actual thumbnails
										overflow     : 'hidden',
										borderRadius : '13px',
										background   : 'transparent'
									}}
									alt=''
								/>

								{uiState.hover && (
									<Box
										width='100%'
										height='100%'
										sx={{
											position       : 'absolute',
											top            : 0,
											left           : 0,
											display        : 'flex',
											alignItems     : 'center',
											justifyContent : 'center',
											background     : 'rgba(0, 0, 0, 0.6)',
											borderRadius   : '13px'
										}}
									>
										<Stack direction={'column'} alignItems={'center'}>
											<Box
												width={'36px'}
												borderRadius={'50%'}
												bgcolor={'#D9D9D9'}
												height={'36px'}
												display={'flex'}
												alignItems={'center'}
												justifyContent={'center'}
												sx={{ cursor: 'pointer' }}
												onClick={handleReplaceThumbnail}
											>
												<TbReplace fontSize='var(--pixel20)' />
											</Box>
											<Typography fontSize={'var(--pixel10)'} color={'var(--whiteColor)'}>{language?.replace}</Typography>
										</Stack>
										{hasThumbnail && (
											<Stack direction={'column'} px={2.5} alignItems={'center'}>
												<Box
													width={'36px'}
													borderRadius={'50%'}
													bgcolor={'#D9D9D9'}
													height={'36px'}
													display={'flex'}
													alignItems={'center'}
													justifyContent={'center'}
													sx={{ cursor: 'pointer' }}
													onClick={handleDeleteThumbnail}
												>
													<RiDeleteBinLine fontSize='var(--pixel20)' />
												</Box>
												<Typography fontSize={'var(--pixel10)'} color={'var(--whiteColor)'}>{language?.delete}</Typography>
											</Stack>
										)}
										{/* ENHANCED: Crop functionality now working */}
										<Stack direction={'column'} alignItems={'center'}>
											<Box
												width={'36px'}
												borderRadius={'50%'}
												bgcolor={'#D9D9D9'}
												height={'36px'}
												display={'flex'}
												alignItems={'center'}
												justifyContent={'center'}
												sx={{ cursor: 'pointer' }}
												onClick={handleCropThumbnail} // ENHANCED: Real crop handler
											>
												<MdCrop fontSize='var(--pixel20)' />
											</Box>
											<Typography fontSize={'var(--pixel10)'} color={'var(--whiteColor)'}>{language?.crop}</Typography>
										</Stack>
									</Box>
								)}
							</Box>
						)
						: (

							/* NEW: Show upload prompt when no thumbnail exists */
							<Box
								my={1}
								sx={{
									width           : '250px',
									height          : '150px',
									border          : '2px dashed #ccc',
									borderRadius    : '13px',
									display         : 'flex',
									alignItems      : 'center',
									justifyContent  : 'center',
									cursor          : 'pointer',
									backgroundColor : '#f9f9f9',
									'&:hover'       : {
										borderColor     : '#24a4c4',
										backgroundColor : '#f0f8ff'
									}
								}}
								onClick={handleReplaceThumbnail}
							>
								<Stack direction={'column'} alignItems={'center'} spacing={1}>
									<TbReplace fontSize='24px' color='#666' />
									<Typography fontSize={'12px'} color={'#666'} textAlign={'center'}>
										Click to upload thumbnail
									</Typography>
								</Stack>
							</Box>
						)}

					{/* Upload Dialog for selecting thumbnails */}
					<UploadDialog
						open={uiState.uploadDialogOpen}
						onClose={() => updateUIState({ uploadDialogOpen: false })}
						fileType='Image'
						note={language?.supportformate}
						onFileSelect={(url: string, source?: string, key?: string) =>
						{
							// Set replacing thumbnail state to suppress delete toast
							updateUIState({ isReplacingThumbnail: true });

							// If we're replacing an existing thumbnail from S3, delete the old one without showing toast
							if (currentData.thumbnail?.source === 'system' && currentData.thumbnail?.key)
							{
								deleteFileFromS3(currentData.thumbnail.key, false); // Suppress delete toast
							}
							handleThumbnailUpdate(url, source, key);
							updateUIState({ uploadDialogOpen: false, isReplacingThumbnail: false });
						}}
						Embednote='Only Upload JPEG, JPG and PNG formats.'
					/>

					{/* ENHANCED: Real Crop Dialog with react-advanced-cropper */}
					<CropDialog
						open={uiState.cropDialogOpen}
						imageUrl={currentData.thumbnail?.url || ''}
						onClose={handleCropCancel}
						onCropComplete={handleCropComplete}
						language={language}
					/>

					{/* Delete confirmation dialog */}
					<NewDialogPopup
						open={uiState.deleteDialogOpen}
						handleConfirm={() => updateUIState({ deleteDialogOpen: false })}
						type={language?.no || 'No'}
						id={undefined}
						label={`${language?.LikeToDeleteThe || 'Would you like to delete the'} thumbnail?`}
						from='feedCreate'
						formatType={language?.yes || 'Yes'}
						closeCallBack={async () =>
						{
							// Check if the thumbnail is from S3 and has a key
							if (currentData.thumbnail?.source === 'system' && currentData.thumbnail?.key)
							{
								await deleteFileFromS3(currentData.thumbnail.key);
							}

							// Reset thumbnail state
							handleThumbnailUpdate('');
							updateUIState({ deleteDialogOpen: false });
						}}
					/>
				</>
			)}
		</>
	);
};

export default DetailsSlider;